interface CompressOptions {
    maxWidth?: number;
    maxHeight?: number;
    maxSizeKB: number;
    maxQuality?: number;
    fixedQuality?: number | null;
    cropWidth?: number | null;
    cropHeight?: number | null;
}

interface ImageDimensions {
    width: number;
    height: number;
    aspectRatio: number;
}

interface CompressResult {
    file: Blob;
    width: number;
    height: number;
    size: number;
    quality: number;
}

export class ImageCompressor {
    private static readonly FORMAT = 'image/webp';
    private static readonly MIN_QUALITY = 0.1;
    private static readonly MAX_QUALITY = 0.9;
    private static readonly MAX_ASPECT_RATIO = 5.25; // 21:4 aspect ratio

    private static estimateInitialQuality(width: number, height: number, targetSizeKB: number, originalSize: number): number {
        const pixels = width * height;
        const targetBytes = targetSizeKB * 1024;
        const compressionRatio = originalSize / pixels;
        
        let estimatedQuality;
        if (compressionRatio < 0.5) {
            estimatedQuality = (targetBytes * 15) / pixels;
            estimatedQuality *= 0.8;
        } else {
            estimatedQuality = (targetBytes * 25) / pixels;
        }
        
        const maxQuality = pixels < 1000000 ? 0.85 : this.MAX_QUALITY;
        return Math.min(Math.max(estimatedQuality, this.MIN_QUALITY), maxQuality);
    }

    private static createBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
        return new Promise((resolve) => {
            canvas.toBlob(
                (blob) => resolve(blob!),
                this.FORMAT,
                quality
            );
        });
    }

    private static validateAspectRatio(width: number, height: number): void {
        const aspectRatio = width / height;
        if (aspectRatio > this.MAX_ASPECT_RATIO || aspectRatio < 1 / this.MAX_ASPECT_RATIO) {
            throw new Error(
                `Image aspect ratio (${aspectRatio.toFixed(2)}) exceeds the allowed range. ` +
                `Must be between 1:${this.MAX_ASPECT_RATIO} and ${this.MAX_ASPECT_RATIO}:1`
            );
        }
    }

    private static calculateDimensionsForCrop(originalWidth: number, originalHeight: number, cropWidth: number, cropHeight: number): { width: number; height: number } {
        const originalAspect = originalWidth / originalHeight;
        const cropAspect = cropWidth / cropHeight;
        
        let targetWidth: number;
        let targetHeight: number;
        
        if (originalAspect > cropAspect) {
            // Image is wider than crop - height determines scale
            targetHeight = cropHeight;
            targetWidth = Math.round(targetHeight * originalAspect);
        } else {
            // Image is taller than crop - width determines scale
            targetWidth = cropWidth;
            targetHeight = Math.round(targetWidth / originalAspect);
        }
        
        return { width: targetWidth, height: targetHeight };
    }

    private static async getImageDimensions(file: File): Promise<ImageDimensions> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            const imageUrl = URL.createObjectURL(file);
            
            img.onload = () => {
                URL.revokeObjectURL(imageUrl);
                const dimensions = {
                    width: img.width,
                    height: img.height,
                    aspectRatio: img.width / img.height
                };
                resolve(dimensions);
            };
            
            img.onerror = () => {
                URL.revokeObjectURL(imageUrl);
                reject(new Error('Failed to load image'));
            };
            
            img.src = imageUrl;
        });
    }

    private static async compress(file: File, options: CompressOptions): Promise<CompressResult> {
        const { 
            maxWidth, 
            maxHeight, 
            maxSizeKB, 
            maxQuality = this.MAX_QUALITY, 
            fixedQuality = null,
            cropWidth = null,
            cropHeight = null 
        } = options;
        
        const img = new Image();
        const imageUrl = URL.createObjectURL(file);
        
        try {
            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
                img.src = imageUrl;
            });

            let width: number;
            let height: number;
            
            if (cropWidth && cropHeight) {
                // Calculate dimensions that ensure the crop area will be at full resolution
                const dims = this.calculateDimensionsForCrop(img.width, img.height, cropWidth, cropHeight);
                width = dims.width;
                height = dims.height;
            } else {
                width = img.width;
                height = img.height;
                const aspectRatio = width / height;

                if (maxWidth && width > maxWidth) {
                    width = maxWidth;
                    height = Math.round(width / aspectRatio);
                }

                if (maxHeight && height > maxHeight) {
                    height = maxHeight;
                    width = Math.round(height * aspectRatio);
                }
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                throw new Error('Could not get canvas context');
            }

            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            
            ctx.drawImage(img, 0, 0, width, height);

            // If fixedQuality is provided, use that instead of binary search
            if (fixedQuality !== null) {
                const blob = await this.createBlob(canvas, fixedQuality);
                return {
                    file: blob,
                    width,
                    height,
                    size: blob.size,
                    quality: fixedQuality,
                };
            }

            let low = this.MIN_QUALITY;
            let high = Math.min(
                this.estimateInitialQuality(width, height, maxSizeKB, file.size),
                maxQuality
            );
            
            const initialBlob = await this.createBlob(canvas, 0.5);
            if (initialBlob.size > maxSizeKB * 1024) {
                high = 0.5;
            }

            let bestBlob: Blob | null = null;
            let bestQuality = low;
            const targetSize = maxSizeKB * 1024;

            while (high - low > 0.01) {
                const mid = (low + high) / 2;
                const blob = await this.createBlob(canvas, mid);

                if (blob.size <= targetSize) {
                    bestBlob = blob;
                    bestQuality = mid;
                    low = mid;
                } else {
                    high = mid;
                }
            }

            if (!bestBlob) {
                bestBlob = await this.createBlob(canvas, this.MIN_QUALITY);
                bestQuality = this.MIN_QUALITY;
            }

            return {
                file: bestBlob,
                width,
                height,
                size: bestBlob.size,
                quality: bestQuality,
            };

        } finally {
            URL.revokeObjectURL(imageUrl);
        }
    }

    static async processImage(file: File): Promise<{ largeVersion: CompressResult; smallVersion: CompressResult }> {
        const dimensions = await this.getImageDimensions(file);
        this.validateAspectRatio(dimensions.width, dimensions.height);

        // Process large version (1000x800 for 500x400 display with 2x DPI)
        const largeVersion = await this.compress(file, {
            cropWidth: 500 * 2,
            cropHeight: 400 * 2,
            maxSizeKB: 80
        });

        // Calculate maximum allowed size for small version
        const maxSmallSizeKB = Math.min(
            30, // Original max size for small version
            (largeVersion.size / 1024) * 0.8 // 80% of large version size
        );

        // Process small version (636x444 for 318x222 display with 2x DPI)
        const smallVersion = await this.compress(file, {
            maxSizeKB: maxSmallSizeKB,
            maxQuality: largeVersion.quality, // Never use higher quality than large version
            cropWidth: 318 * 2,
            cropHeight: 222 * 2
        });

        return { largeVersion, smallVersion };
    }
}